import React from "react";

import { Circle, G, Line, Text as SvgText } from "react-native-svg";

import { useGameplayStore } from "../../game/stores/useGameplayStore";
import { useDevSession } from "../../dev/devSession";
import { nextBestMove, remainingBestRoute } from "../../dev/bestMove";
import type { BoardLayout } from "../board/boardLayout";
import { project } from "../board/layout";
import { OPTION_R } from "../board/options";
import { strokeWidthProps } from "../board/strokes";
import { DEV_COLOR } from "./devStyles";

type Graph = Record<string, { tsne?: number[] | [number, number] } | undefined>;
type Pt = { px: number; py: number; r: number };

/** Where a word sits on the board: its placed node/option, else its projection. */
export function boardPoint(
  layout: BoardLayout,
  graph: Graph,
  w: string,
): Pt | null {
  const placed =
    layout.nodes.find((n) => n.id === w) ??
    layout.options.find((o) => o.id === w);
  if (placed) return { px: placed.px, py: placed.py, r: placed.r };
  const t = graph[w]?.tsne;
  return t ? { ...project(layout.camera, t[0], t[1]), r: OPTION_R } : null;
}

/** Keep a marker inside the board (margin px); flags when it was moved. */
export function clampToBoard(
  p: Pt,
  size: { w: number; h: number },
  margin = 24,
): Pt & { off: boolean } {
  const px = Math.max(margin, Math.min(size.w - margin, p.px));
  const py = Math.max(margin, Math.min(size.h - margin, p.py));
  return { ...p, px, py, off: px !== p.px || py !== p.py };
}

/**
 * Dev-only "show best move" on the board: the rest of the best route
 * as a faint dashed line and a dashed magenta ring on the next word, labelled
 * "dev". Drawn inside the camera transform; never touches the game.
 */
const DevBoardOverlay: React.FC<{
  layout: BoardLayout;
  graph: Graph;
  size: { w: number; h: number };
  unitsPerPx: number;
}> = ({ layout, graph, size, unitsPerPx: upp }) => {
  const show = useDevSession((s) => s.showBestMove);
  const g = useGameplayStore();
  if (!show || g.gameStatus !== "playing") return null;
  const st = {
    currentWord: g.currentWord,
    targetWord: g.targetWord,
    goldPath: g.optimalPath,
    localOptimalRoute: g.suggestedPathFromCurrent,
  };
  const pts = remainingBestRoute(st)
    .map((w) => boardPoint(layout, graph, w))
    .filter((p): p is Pt => !!p);
  const nextWord = nextBestMove(st);
  const at = nextWord ? boardPoint(layout, graph, nextWord) : null;
  const next = at ? clampToBoard(at, size) : null;
  const dash = { strokeDasharray: "5 4", stroke: DEV_COLOR, fill: "none" };
  return (
    <G pointerEvents="none" testID="dev-optimal-overlay">
      {pts.slice(1).map((p, i) => (
        <Line
          key={`dev-seg-${i}`}
          x1={pts[i].px}
          y1={pts[i].py}
          x2={p.px}
          y2={p.py}
          opacity={0.4}
          {...dash}
          {...strokeWidthProps(2, upp)}
        />
      ))}
      {next && (
        <G testID={`dev-optimal-next-${nextWord}`}>
          <Circle
            cx={next.px}
            cy={next.py}
            r={next.r + 6 * upp}
            {...dash}
            {...strokeWidthProps(2, upp)}
          />
          <SvgText
            x={next.px}
            y={next.py - next.r - 10 * upp}
            fontSize={9 * upp}
            fontWeight="700"
            fill={DEV_COLOR}
            textAnchor={
              !next.off ? "middle" : next.px > size.w / 2 ? "end" : "start"
            }
          >
            {next.off ? `DEV next: ${nextWord} (off-board)` : "DEV next"}
          </SvgText>
        </G>
      )}
    </G>
  );
};

export default DevBoardOverlay;
